// Redux Utils
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "@/features/auth/authThunks";

// React Utils
import { useState, useRef } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import SignInImage from "@/assets/FormSignIn.webp";
import LogoAppWhoop from "@/assets/appwhoop_ticket_logo.webp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Framer Motion
import { motion as Motion, AnimatePresence } from "framer-motion";
import { loginReset } from "@/features/auth/authSlice";

// Context
import { useModalsContext } from "@/contexts/ModalsContext";

export default function SignIn({ onClose, onRegisterClick }) {
  // redux dispatch
  const dispatch = useDispatch();
  const { isAuthenticated, error, isLoading } = useSelector((state) => state.auth);
  // console.log("Auth state:", { isAuthenticated, error, isLoading });

  // modals context
 const { closeModal } = useModalsContext();

  // local state
  const [showPassword, setShowPassword] = useState(false);
  const passwordInputRef = useRef(null);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    email: "",
    password: "",
    remember: false,
  });
  const navigate = useNavigate();

  // Validación básica
  const validate = () => {
    const newErrors = {};
    if (!form.email) {
      newErrors.email = "El email es obligatorio";
    }
    if (!form.password) {
      newErrors.password = "La contraseña es obligatoria";
    }
    if (form.password && form.password.length < 8) {
      newErrors.password = "La contraseña debe tener al menos 8 caracteres";
    }
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = "El email no es válido";
    }
    if (form.email && form.email.length > 50) {
      newErrors.email = "El email no puede tener más de 50 caracteres";
    }
    if (form.password && form.password.length > 50) {
      newErrors.password = "La contraseña no puede tener más de 50 caracteres";
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      try {
        // debugger;
        const result = dispatch(loginUser({ username: form.email, password: form.password }));
        
        // Only close modal if login is successful
        await result.unwrap();
        window.location.reload();
      } catch (error) {
        console.error("Login error:", error);
      }
    }
  };
  if (isAuthenticated) return <Navigate to="/" replace />;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 bg-aw-bgbluedark-30 rounded-lg max-h-[90vh] overflow-hidden shadow-lg">
      <div className="hidden sm:block">
        <img
          src={SignInImage}
          alt="Sign In"
          className="w-full h-full object-cover object-center rounded-t-2xl sm:rounded-l-rounded-t-2xl"
        />
      </div>
      <div
        className={`p-8 flex flex-col justify-center bg-aw-bluedark rounded-b-rounded-t-2xl sm:rounded-r-rounded-t-2xl text-white relative`}
      >
        {/* Mobile background image with overlay */}
        <div className="absolute inset-0 sm:hidden">
          <img
            src={SignInImage}
            alt="Sign In"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-aw-bluedark-75 backdrop-blur-[1.5px]" />
        </div>
        <div className="relative z-10">
          <div className="w-32 mb-4">
            <img
              src={LogoAppWhoop}
              alt="AppWhoop Logo"
              className="w-full h-auto object-contain"
            />
          </div>
          <h2 className="text-lg font-bold mb-6">
            Haz inicio de sesión con tu perfil
          </h2>
          {/* Form de inicio de sesión normal */}
          {error == null ? (
            <form onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-1"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                  name="email"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-white text-black focus:outline-none focus:ring-2 focus:ring-aw-bluedark"
                  placeholder="Email"
                />
              </div>
              {errors.email && (
                <span className="text-xs text-red-500">{errors.email}</span>
              )}
              <div className="relative mt-2">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium mb-1"
                >
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    ref={passwordInputRef}
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={form.password}
                    onChange={handleChange}
                    autoComplete="current-password"
                    name="password"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-white text-black focus:outline-none focus:ring-2 focus:ring-aw-bluedark pr-10"
                    placeholder="Contraseña"
                  />
                  <button
                    type="button"
                    className="h-full w-1/12 absolute top-0 right-1 flex justify-center items-center text-gray-500 hover:text-aw-bluedark focus:outline-none duration-200 px-2"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      // Guardar posición del cursor
                      const input = passwordInputRef.current;
                      const start = input.selectionStart;
                      const end = input.selectionEnd;
                      setShowPassword((prev) => !prev);
                      setTimeout(() => {
                        input.focus();
                        input.setSelectionRange(start, end);
                      }, 0);
                    }}
                    tabIndex={-1}
                  >
                    <AnimatePresence>
                      <Motion.div
                        key={showPassword ? "eye-slash" : "eye"}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <FontAwesomeIcon
                          icon={
                            showPassword
                              ? "fa-regular fa-eye-slash"
                              : "fa-regular fa-eye"
                          }
                          className="absolute top-3 right-3"
                        />
                      </Motion.div>
                    </AnimatePresence>
                  </button>
                </div>
              </div>
              {errors.password && (
                <span className="text-xs text-red-500">{errors.password}</span>
              )}
              <div className="flex items-center justify-between mt-2 mb-8">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="remember"
                    className="h-4 w-4 text-aw-bluedark focus:ring-aw-bluedark border-gray-300 rounded"
                  />
                  <label htmlFor="remember" className="ml-2 block text-sm">
                    Recuérdame
                  </label>
                </div>
                {/* link para indicar que olvidó la contraseña */}
                <div className="text-sm">
                  <Link to="/forgot-password" className="text-aw-green hover:underline">
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              </div>

              {/* Boton de submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full text-lg bg-aw-green text-black font-medium py-1 px-4 rounded transition-scale duration-300 hover:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Iniciando sesión..." : "Iniciar Sesión"}
              </button>
            </form>
          ) : (
            <div className="text-amber-500 text-center mt-2">
              <FontAwesomeIcon
                icon="fa-solid fa-user-lock"
                className="text-4xl mx-auto mb-2 animate-bounce"
              />
              <h4 className="text-2xl text-amber-50 font-semibold">
                {error.message}
              </h4>
              <button
                onClick={() => dispatch(loginReset())}
                className="mt-4 px-4 py-2 bg-aw-green text-aw-dark rounded-lg font-semibold hover:bg-aw-green/95 hover:scale-95 transition-transform duration-300"
              >
                Volver a Intentar
              </button>
              <span className="text-xs my-4 text-amber-50 duration-500 block mx-auto">
                Si ya cuentas con un código para confirmar tu cuenta,{" "}
                <button
                  onClick={() => {
                    dispatch(loginReset());
                    setTimeout(() => {
                      navigate("/confirmar-usuario");
                    }, 1000);
                  }}
                  className="text-aw-green hover:underline font-semibold ml-1"
                >
                  haz clic aquí
                </button>
              </span>
              <span className="text-xs mt-4 text-amber-50 duration-500 block mx-auto">
                Si no has recibido el código de confirmación o{" "}
                <span className="font-semibold">ha expirado</span>
                <button
                  onClick={() => {
                    dispatch(loginReset());
                    setTimeout(() => {
                      navigate("/reenviar-codigo-confirmar-usuario");
                    }, 1000);
                  }}
                  className="text-amber-400 hover:underline font-semibold ml-1"
                >
                  haz clic aquí
                </button>
              </span>
            </div>
          )}

          {/* Opciones para ingresar con google y para registrarse */}
          <hr className="my-6 border-gray-300" />
          {/* <div className="flex items-center justify-center gap-2">
            <span className="text-xs">O inicia sesión con:</span>
            <FontAwesomeIcon
              icon="fa-brands fa-apple"
              className="text-xl transition-scale bg-aw-green text-aw-dark rounded-full py-2 px-2.5 duration-300 transition-scale hover:scale-85 cursor-pointer"
            />
            <FontAwesomeIcon
              icon="fa-brands fa-google"
              className="text-xl transition-scale bg-aw-green text-aw-dark rounded-full py-2 px-2.5 duration-300 transition-scale hover:scale-85 cursor-pointer"
            />
          </div> */}
          <div className="text-center mt-4">
            <p className="text-sm">
              ¿No tienes una cuenta?{" "}
              <span
                href="#"
                onClick={onRegisterClick}
                className="text-aw-green hover:underline font-semibold cursor-pointer"
              >
                Regístrate Gratis
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
